import React, { useEffect, useState } from 'react';
import { Trophy } from 'lucide-react';
import client from '../lib/api';
import GameArtwork from './GameArtwork';
import { SECTIONS, sectionHook } from '../lib/domHooks';

/* Katalog sayfalarının altındaki "Yüksek Kazançlar" şeridi.
 *
 * Satırlar **gerçek turlardan** gelir (`/games/recent-wins?scope=high`) ve
 * kullanıcı adı sunucuda maskelenir (`kad***91`) — "Son Kazananlar" şeridi ve
 * canlı bahis tablosuyla aynı maske, aynı gerekçe: aynı oyuncu iki listede iki
 * farklı adla görünmemelidir. Yanıtta üye kimliği yoktur.
 *
 * **Yoklama yoktur, bilinçli olarak.** Uygulama tek uvicorn worker ile çalışır
 * (bkz. CLAUDE.md → Dikkat edilecekler); iki katalog sayfasında ziyaretçi
 * başına dönen bir zamanlayıcı, sağlayıcı webhook'larıyla aynı süreci
 * gereksiz yere yorar. Şerit her açılışta tazelenir ve sunucu penceredeki en
 * yüksek turlardan rastgele bir demet seçtiği için liste ziyaretten ziyarete
 * değişir.
 *
 * Kazançlı tur yoksa bölüm **hiç çizilmez**: uydurma kazanç satırı üretmek
 * oyuncuya yalan söylemek olurdu.
 *
 * **Vega şemasında uç ayrı bir sözleşme uygular** (bkz. CLAUDE.md → "Katalog
 * sayfalarının kendi vitrini vardır"): adlar yine gerçek üyelerden gelir ve
 * aynı maskeden geçer, oyunlar açık katalogdandır, ama tutar her istekte
 * üretilir. Ayrım **sunucudadır** — bileşen şemaya göre çatallanmaz ve
 * gelen satırı olduğu gibi çizer.
 *
 * **Şerit durmadan kayar.** Döngünün dikişsiz olması için liste iki kez
 * çizilir ve animasyon tam yarıda başa döner — izleyen için kopma yoktur.
 * Kısa listede (yeni kurulumda üç kazanç olabilir) şerit ekranı dolduramaz
 * ve kaymak yerine sıçrıyor gibi görünürdü, bu yüzden liste önce
 * `MIN_TRACK` uzunluğuna kadar tekrarlanır. Hız kart sayısından bağımsız
 * sabittir: süre kart başına verilir, yoksa uzun liste hızlanırdı.
 *
 * **`category` şeridi sayfanın kataloğuna daraltır.** Canlı casino sayfasının
 * altında slot kazançları görünüyordu; sayfa kendi kategorisini verir ve
 * sunucu yalnızca o kategorideki oyunların turlarını döndürür. O kategoride
 * kazanç yoksa şerit (yukarıdaki kuralla) hiç çizilmez. Kategorisiz çağrı
 * (Vega ana sayfası) bütün turlara bakmaya devam eder.
 */

const LIMIT = 12;

// Dikişsiz döngü için rayın en az bir ekran genişliğinde olması gerekir.
const MIN_TRACK = 14;

// Kart başına saniye. Toplam süre kart sayısıyla çarpılır, böylece şeridin
// hızı liste uzunluğundan bağımsız kalır.
const SECONDS_PER_CARD = 3.2;

const money = (value) => `${Number(value || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} ₺`;

const TopWinsStrip = ({ title = 'Yüksek Kazançlar', category = '' }) => {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let active = true;
    const params = { scope: 'high', limit: LIMIT };
    if (category) params.category = category;
    client.get('/games/recent-wins', { params })
      .then(({ data }) => {
        if (active) setRows(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        // Yan bir vitrin öğesidir; geçici ağ hatasında sayfaya hata basmak
        // gürültü olurdu, şerit çizilmez.
      });
    return () => { active = false; };
  }, [category]);

  if (rows.length === 0) return null;

  // Ray iki kopyadan oluşur; animasyon `-50%`'de başa döndüğü için ikinci
  // kopya birincinin yerine tam oturur ve dikiş görünmez.
  const base = [];
  while (base.length < MIN_TRACK) base.push(...rows);
  const track = base.concat(base);

  return (
    <section className="top-wins" aria-label={title} {...sectionHook(SECTIONS.WINS)}>
      <div className="top-wins-head">
        <Trophy size={16} aria-hidden="true" />
        <h2 className="top-wins-title">{title}</h2>
      </div>
      <div className="top-wins-rail">
        <div
          className="top-wins-track"
          style={{ animationDuration: `${(base.length * SECONDS_PER_CARD).toFixed(1)}s` }}
        >
        {track.map((row, i) => (
          <article
            className="top-wins-card"
            key={i}
            /* İkinci kopya ekran okuyucuya tekrar okunmaz; aynı kazanç iki
               kez duyurulurdu. */
            aria-hidden={i >= base.length ? 'true' : undefined}
          >
            <div className="top-wins-art">
              <GameArtwork src={row.image} alt={row.game_name} />
            </div>
            <div className="top-wins-info">
              <div className="top-wins-amount">{money(row.win)}</div>
              <div className="top-wins-user">{row.user}</div>
              <div className="top-wins-game" title={row.game_name}>{row.game_name}</div>
            </div>
          </article>
        ))}
        </div>
      </div>
    </section>
  );
};

export default TopWinsStrip;
